/* 把儲存層接到 React。狀態集中在這裡，畫面只負責顯示與呼叫動作。 */
import { useCallback, useEffect, useRef, useState } from "react";
import type {
  Category, Concept, InboxItem, KnowledgeLink, OpReason, Settings,
} from "../domain/types.ts";
import type { ConceptPatch, IdbRepository } from "../data/repository.ts";
import { openRepository } from "../data/repository.ts";
import { newId } from "../domain/id.ts";
import { nextReviewDue, overdueConcepts, visibleConcepts, searchConcepts } from "../domain/rules.ts";
import type { SearchResult } from "../domain/rules.ts";

export interface LifesageState {
  ready: boolean;
  error: string | null;
  categories: Category[];
  concepts: Concept[];
  links: KnowledgeLink[];
  inbox: InboxItem[];
  settings: Settings;
}

const EMPTY: LifesageState = {
  ready: false, error: null, categories: [], concepts: [], links: [], inbox: [],
  settings: { deviceId: "", deviceName: "這台裝置", locale: "auto", sheetMaterial: "night",
    typeScale: 1, adultUnlocked: false, reviewDefaultDays: 365 },
};

function deviceId(): string {
  /* ?device=dev-A：讓同一台瀏覽器上的不同資料庫扮演不同裝置（驗收多裝置同步用）。 */
  const override = new URLSearchParams(location.search).get("device");
  if (override) return override;
  const KEY = "lifesage.deviceId";
  let id = localStorage.getItem(KEY);
  if (!id) { id = "dev-" + newId(); localStorage.setItem(KEY, id); }
  return id;
}

export function useLifesage() {
  const [state, setState] = useState<LifesageState>(EMPTY);
  const [repo, setRepo] = useState<IdbRepository | null>(null);
  const repoRef = useRef<IdbRepository | null>(null);
  const opening = useRef(false);

  const reload = useCallback(async () => {
    const repo = repoRef.current;
    if (!repo) return;
    const [categories, concepts, links, inbox, settings] = await Promise.all([
      repo.listCategories(), repo.listConcepts(true), repo.listLinks(), repo.listInbox(), repo.getSettings(),
    ]);
    setState({ ready: true, error: null, categories, concepts, links, inbox, settings });
  }, []);

  useEffect(() => {
    if (opening.current) return;
    opening.current = true;
    openRepository(indexedDB, deviceId(),
      Date.now, new URLSearchParams(location.search).get("db") ?? undefined)
      .then(async (opened) => {
        repoRef.current = opened;
        setRepo(opened);
        await opened.purgeExpiredTrash();
        await reload();
      })
      .catch((e) => setState((s) => ({ ...s, ready: true, error: String(e) })));
  }, [reload]);

  const createConcept = useCallback(async (patch: ConceptPatch) => {
    const repo = repoRef.current;
    if (!repo) throw new Error("儲存層還沒準備好");
    const res = await repo.saveConcept(null, { ...patch, status: "active" });
    await reload();
    return res.concept;
  }, [reload]);

  const updateConcept = useCallback(async (concept: Concept, patch: ConceptPatch) => {
    const repo = repoRef.current;
    if (!repo) throw new Error("儲存層還沒準備好");
    const res = await repo.saveConcept(concept, patch);
    await reload();
    return res.concept;
  }, [reload]);

  const setStatus = useCallback(async (concept: Concept, status: Concept["status"]) => {
    const repo = repoRef.current;
    if (!repo) throw new Error("儲存層還沒準備好");
    await repo.setConceptStatus(concept.id, status);
    await reload();
  }, [reload]);

  const restoreVersion = useCallback(async (concept: Concept, rev: number) => {
    const repo = repoRef.current;
    if (!repo) throw new Error("儲存層還沒準備好");
    const c = await repo.restoreConcept(concept.id, rev);
    await reload();
    return c;
  }, [reload]);

  const historyOf = useCallback(async (concept: Concept) => {
    const repo = repoRef.current;
    if (!repo) return [];
    return repo.history(concept.id);
  }, []);

  /** 複習：還準確就延長一個週期，並留下紀錄。 */
  const reviewKeep = useCallback(async (concept: Concept) => {
    const repo = repoRef.current;
    if (!repo) throw new Error("儲存層還沒準備好");
    const prevDueAt = concept.reviewDueAt;
    const due = nextReviewDue(Date.now(), concept.reviewIntervalDays);
    await repo.saveConcept(concept, { reviewDueAt: due }, "review");
    await repo.logReview({ conceptId: concept.id, at: Date.now(), action: "keep", prevDueAt, nextDueAt: due });
    await reload();
  }, [reload]);

  const emptyTrash = useCallback(async () => {
    const repo = repoRef.current;
    if (!repo) throw new Error("儲存層還沒準備好");
    const n = await repo.purgeExpiredTrash(true);
    await reload();
    return n;
  }, [reload]);

  const createLink = useCallback(async (fromId: string, toId: string, type: string, note: string) => {
    const repo = repoRef.current;
    if (!repo) throw new Error("儲存層還沒準備好");
    const link = await repo.saveLink({ fromId, toId, type, note });
    await reload();
    return link;
  }, [reload]);

  const deleteLink = useCallback(async (id: string) => {
    const repo = repoRef.current;
    if (!repo) throw new Error("儲存層還沒準備好");
    await repo.deleteLink(id);
    await reload();
  }, [reload]);

  /** 類別的每一個改動都會寫一筆 op 並進待推送佇列 —— 所以會同步到其他裝置。 */
  const saveCategory = useCallback(async (
    patch: Parameters<IdbRepository["saveCategory"]>[0], reason?: OpReason) => {
    const repo = repoRef.current;
    if (!repo) throw new Error("儲存層還沒準備好");
    const saved = await repo.saveCategory(patch, reason);
    await reload();
    return saved;
  }, [reload]);

  const deleteCategory = useCallback(async (id: string) => {
    const repo = repoRef.current;
    if (!repo) throw new Error("儲存層還沒準備好");
    const moved = await repo.deleteCategory(id);
    await reload();
    return moved;
  }, [reload]);

  const saveSettings = useCallback(async (patch: Partial<Settings>) => {
    const repo = repoRef.current;
    if (!repo) throw new Error("儲存層還沒準備好");
    const next = await repo.saveSettings(patch);
    setState((s) => ({ ...s, settings: next }));
    return next;
  }, []);

  /* 主視圖含草稿（要讓你找得到、補得完），複習與搜尋則不含草稿。 */
  const visible = visibleConcepts(state.concepts, { adultUnlocked: state.settings.adultUnlocked, includeDraft: true });
  const due = overdueConcepts(visible, Date.now());
  const search = useCallback(
    (q: string): SearchResult[] =>
      searchConcepts(state.concepts, q, { adultUnlocked: state.settings.adultUnlocked }),
    [state.concepts, state.settings.adultUnlocked],
  );

  return { state, repo, visible, due, search, reload,
    actions: { createConcept, updateConcept, setStatus, restoreVersion, historyOf, reviewKeep, saveSettings,
      emptyTrash, createLink, deleteLink, saveCategory, deleteCategory } };
}
